import { useId } from 'react';
import { ComboField } from '~/components/form/ComboField';

interface SelectProps {
  options: { value: string; label: string }[];
  /** Controlled: the current value; "" shows the placeholder. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  /** Becomes the input's id so a sibling <label htmlFor> resolves to it. */
  id?: string;
  /** Renders the field frame's label line; omit when a sibling `FieldLabel` or `FormField` supplies it. */
  label?: string;
  helperText?: string;
  required?: boolean;
  className?: string;
  /** data-testid; defaults to `id`. */
  testId?: string;
  disabled?: boolean;
  /**
   * Lets the placeholder be picked again after a value has been chosen. Off by default, because
   * "Select an option" is not a choice — but a filter's "All actions" is, and a one-way filter
   * is a bug.
   */
  placeholderSelectable?: boolean;
}

/** The one fixed-option dropdown: controlled, searchable. */
export const Select = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  ariaLabel,
  id,
  label,
  helperText,
  required,
  className,
  testId,
  disabled,
  placeholderSelectable,
}: SelectProps) => {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <ComboField
      id={fieldId}
      testId={testId ?? id}
      label={label}
      helperText={helperText}
      required={required}
      ariaLabel={ariaLabel ?? label ?? placeholder}
      className={className}
      value={value}
      options={placeholderSelectable ? [{ value: '', label: placeholder }, ...options] : options}
      placeholder={placeholder}
      searchable
      disabled={disabled}
      onChange={onChange}
    />
  );
};
